/** 관리자·판매업체에서 체결한 약관·계약서 미리보기(새 창) */

import {
  parseContractBodySegments,
  parseContractSections,
} from '@/utils/parseContractSections'
import {
  CONTRACT_PDF_MESSAGE,
  downloadContractPdf,
  type ContractPdfParty,
} from '@/utils/contractPdf'

/** 관리자 미리보기에서 체결 저장본 PDF를 원본 창으로 요청 */
export const STORED_CONTRACT_PDF_MESSAGE = 'jangi-download-stored-contract-pdf'

export type LegalAcceptanceView = {
  idx?: number
  code: string
  version: string
  title?: string
  body?: string
  kind?: 'TERMS' | 'CONTRACT'
  signedAt?: string | null
  signature?: string
  hasPdf?: boolean
  pdfPath?: string
}

function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function kindLabel(kind?: string) {
  return kind === 'CONTRACT' ? '계약서' : '이용약관'
}

function segmentsHtml(body: string) {
  return parseContractBodySegments(body)
    .map((seg) => {
      if (seg.type === 'prose') {
        return `<pre class="body">${escapeHtml(seg.text)}</pre>`
      }
      const head = `<tr>${seg.headers.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}</tr>`
      const rows = seg.rows
        .map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(c || '')}</td>`).join('')}</tr>`)
        .join('')
      return `<div class="tw"><table class="tbl">${head}${rows}</table></div>`
    })
    .join('')
}

function renderContractBody(body: string) {
  const groups = parseContractSections(body)
  if (!groups.length) {
    return `<pre class="body">${escapeHtml(body || '(본문 없음)')}</pre>`
  }
  return groups
    .filter((group) => group.kind !== 'intro')
    .map((group) => {
      if (group.kind === 'party') {
        return `<section class="sec">
          <h3>${escapeHtml(group.title)}</h3>
          ${segmentsHtml(group.blocks[0]?.body || '')}
        </section>`
      }
      if (group.kind === 'basic') {
        const articles = group.blocks
          .map(
            (block) =>
              `<details class="art" open><summary>${escapeHtml(block.title)}</summary>${segmentsHtml(block.body)}</details>`,
          )
          .join('')
        return `<section class="sec"><h3>${escapeHtml(group.title)}</h3>${articles}</section>`
      }
      return group.blocks
        .map(
          (block) =>
            `<section class="sec"><h3>${escapeHtml(block.title)}</h3>${segmentsHtml(block.body)}</section>`,
        )
        .join('')
    })
    .join('')
}

/** 서명 내역 + 활성 문서를 합친다. 서명 없어도 계약서 본문을 볼 수 있다. */
export function mergeLegalDocViews(
  accepts: LegalAcceptanceView[] = [],
  activeDocs: Array<{
    code: string
    version: string
    title?: string
    body?: string
    kind?: 'TERMS' | 'CONTRACT'
    active?: boolean
  }> = [],
): LegalAcceptanceView[] {
  const views: LegalAcceptanceView[] = []
  const seen = new Set<string>()

  for (const row of accepts) {
    const key = `${row.code}::${row.version}`
    if (seen.has(key)) continue
    seen.add(key)
    views.push(row)
  }

  const acceptedCodes = new Set(accepts.map((row) => row.code))
  for (const doc of activeDocs) {
    if (doc.active === false) continue
    /* 이미 동의한 코드는 체결본을 우선. 동의 전이면 활성 본문을 보여 준다. */
    if (acceptedCodes.has(doc.code)) continue
    const key = `${doc.code}::${doc.version}`
    if (seen.has(key)) continue
    seen.add(key)
    views.push({
      code: doc.code,
      version: doc.version,
      title: doc.title,
      body: doc.body,
      kind: doc.kind === 'CONTRACT' ? 'CONTRACT' : 'TERMS',
      signedAt: null,
      signature: '',
    })
  }

  views.sort((a, b) => {
    const ak = a.kind === 'CONTRACT' ? 0 : 1
    const bk = b.kind === 'CONTRACT' ? 0 : 1
    if (ak !== bk) return ak - bk
    return String(b.signedAt || '').localeCompare(String(a.signedAt || ''))
  })
  return views
}

export function downloadLegalText(doc: { code: string; version: string; body?: string; title?: string }) {
  const body = doc.body || ''
  const blob = new Blob([body], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${doc.code}_${doc.version}.txt`
  anchor.click()
  URL.revokeObjectURL(url)
}

export function openLegalAcceptancesPreview(opts: {
  heading: string
  subtitle?: string
  accepts: LegalAcceptanceView[]
  /** 계약서 PDF 을(판매회사) 란에 넣을 업체 정보 */
  party?: ContractPdfParty
  /** 체결 시 저장된 PDF가 있으면 이 콜백으로 내려받는다 */
  downloadStoredPdf?: (row: LegalAcceptanceView) => Promise<void>
}) {
  const accepts = opts.accepts || []
  if (!accepts.length) return false

  const sections = accepts
    .map((row, index) => {
      const title = escapeHtml(row.title || row.code)
      const kind = kindLabel(row.kind)
      const signed = Boolean(row.signedAt || row.signature)
      const status = signed
        ? `동의 ${escapeHtml(row.signedAt || '-')}`
        : '미서명 · 본문만 열람'
      const meta = `${kind} · ${escapeHtml(row.code)} · v${escapeHtml(row.version)} · ${status}`
      const bodyHtml =
        row.kind === 'CONTRACT'
          ? renderContractBody(row.body || '')
          : `<pre class="body">${escapeHtml(row.body || '(본문을 찾을 수 없습니다. 약관 CMS에서 해당 버전을 확인해 주세요.)')}</pre>`
      const sig = row.signature
        ? `<div class="sig-wrap"><h3>서명</h3><img class="sig" src="${escapeHtml(row.signature)}" alt="서명 이미지" /></div>`
        : signed
          ? '<p class="empty">서명 이미지가 없습니다.</p>'
          : '<p class="empty">아직 서명이 없습니다. 계약서 본문은 확인할 수 있습니다.</p>'
      const pdfAttr =
        row.kind === 'CONTRACT' && row.hasPdf && row.idx && opts.downloadStoredPdf
          ? `data-stored-pdf="${index}"`
          : row.kind === 'CONTRACT'
            ? `data-contract-pdf="${index}"`
            : `data-print="${index}"`
      return `<article class="doc" data-idx="${index}">
        <header>
          <div>
            <h2>${title}</h2>
            <p class="meta">${meta}</p>
          </div>
          <div class="actions">
            <button type="button" class="btn" data-download="${index}">텍스트 저장</button>
            <button type="button" class="btn btn-primary" ${pdfAttr}>PDF로 저장</button>
          </div>
        </header>
        <div class="doc-body" id="doc-body-${index}">${bodyHtml}</div>
        ${sig}
      </article>`
    })
    .join('')

  const bodiesJson = JSON.stringify(
    accepts.map((row) => ({
      code: row.code,
      version: row.version,
      title: row.title || row.code,
      body: row.body || '',
    })),
  )

  const win = window.open('', '_blank')
  if (!win) return false

  const onMessage = (event: MessageEvent) => {
    if (event.source !== win) return
    if (event.origin !== window.location.origin) return
    if (event.data?.type === STORED_CONTRACT_PDF_MESSAGE) {
      const row = accepts[Number(event.data.index)]
      if (!row?.idx || !opts.downloadStoredPdf) return
      void opts.downloadStoredPdf(row).catch((error) => {
        console.error(error)
        window.alert(
          error instanceof Error ? error.message : '저장된 계약서 PDF를 내려받지 못했습니다.',
        )
      })
      return
    }
    if (event.data?.type !== CONTRACT_PDF_MESSAGE) return
    const row = accepts[Number(event.data.index)]
    if (!row || row.kind !== 'CONTRACT') return
    void downloadContractPdf({
      title: row.title || row.code,
      version: row.version,
      body: row.body || '',
      party: opts.party,
      signature: row.signature || '',
      signedAt: row.signedAt || '',
      electronicMethod: Boolean(row.signature || row.signedAt),
    }).catch((error) => {
      console.error(error)
      window.alert('계약서 PDF를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.')
    })
  }
  window.addEventListener('message', onMessage)
  const cleanupTimer = window.setInterval(() => {
    if (!win.closed) return
    window.clearInterval(cleanupTimer)
    window.removeEventListener('message', onMessage)
  }, 800)

  win.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>${escapeHtml(opts.heading)}</title>
<style>
  body{font-family:Pretendard,Apple SD Gothic Neo,sans-serif;margin:0;padding:28px;background:#f5f7fa;color:#1b1f24}
  .card{max-width:920px;margin:0 auto}
  h1{margin:0 0 6px;font-size:1.35rem}
  .lead{margin:0 0 22px;color:#6b7280;font-size:.9rem}
  .doc{background:#fff;border:1px solid #e5e7eb;border-radius:14px;padding:22px;margin:0 0 16px}
  .doc header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin:0 0 12px}
  .actions{display:flex;flex-wrap:wrap;gap:6px}
  h2{margin:0 0 4px;font-size:1.05rem}
  h3{margin:16px 0 8px;font-size:.95rem;color:#111}
  .meta{margin:0;color:#6b7280;font-size:.8125rem}
  .sec{margin:0 0 14px;padding:12px;border:1px solid #eef0f3;border-radius:10px;background:#fafbfc}
  .art{margin:0 0 8px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;overflow:hidden}
  .art summary{cursor:pointer;padding:10px 12px;font-weight:700;font-size:.875rem}
  .art[open] summary{border-bottom:1px solid #eef0f3;background:#f8fafc}
  .body{margin:0;padding:12px;white-space:pre-wrap;word-break:break-word;font:inherit;font-size:.875rem;line-height:1.65}
  .tw{overflow:auto;margin:8px 0}
  .tbl{width:100%;border-collapse:collapse;min-width:520px;font-size:.78rem}
  .tbl th,.tbl td{border:1px solid #d7dde5;padding:7px 8px;vertical-align:top;text-align:left}
  .tbl th{background:#f3f5f8;font-weight:800}
  .btn{flex:none;border:1px solid #d1d5db;background:#fff;border-radius:8px;padding:8px 12px;font:inherit;font-size:.8125rem;font-weight:700;cursor:pointer}
  .btn:hover{background:#f3f4f6}
  .btn-primary{border-color:#1f3a56;background:#1f3a56;color:#fff}
  .btn-primary:hover{background:#162a3f}
  .sig-wrap{margin-top:8px}
  .sig{display:block;width:min(100%,640px);min-height:180px;object-fit:contain;border:1px dashed #c5ccd6;border-radius:12px;background:#fff}
  .empty{margin:0;color:#6b7280;font-size:.875rem}
  @media print{
    body{background:#fff;padding:0}
    .lead,.actions,.btn{display:none!important}
    .doc{border:none;padding:0;margin:0 0 24px;border-radius:0}
    .art{break-inside:avoid}
  }
</style></head><body><div class="card">
  <h1>${escapeHtml(opts.heading)}</h1>
  ${opts.subtitle ? `<p class="lead">${escapeHtml(opts.subtitle)}</p>` : ''}
  ${sections}
</div>
<script>
  const bodies = ${bodiesJson};
  const pdfMessage = ${JSON.stringify(CONTRACT_PDF_MESSAGE)};
  const storedPdfMessage = ${JSON.stringify(STORED_CONTRACT_PDF_MESSAGE)};
  const parentOrigin = ${JSON.stringify(window.location.origin)};
  document.querySelectorAll('[data-download]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.getAttribute('data-download'));
      const doc = bodies[idx];
      if (!doc) return;
      const blob = new Blob([doc.body || ''], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (doc.code || 'legal') + '_' + (doc.version || 'v') + '.txt';
      a.click();
      URL.revokeObjectURL(url);
    });
  });
  document.querySelectorAll('[data-stored-pdf]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.getAttribute('data-stored-pdf'));
      if (!window.opener) {
        window.alert('원본 창이 닫혀 PDF를 저장할 수 없습니다. 미리보기를 다시 열어 주세요.');
        return;
      }
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'PDF 받는 중…';
      window.opener.postMessage({ type: storedPdfMessage, index: idx }, parentOrigin);
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = label || 'PDF로 저장';
      }, 2500);
    });
  });
  document.querySelectorAll('[data-contract-pdf]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.getAttribute('data-contract-pdf'));
      if (!window.opener) {
        window.alert('원본 창이 닫혀 PDF를 저장할 수 없습니다. 미리보기를 다시 열어 주세요.');
        return;
      }
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'PDF 만드는 중…';
      window.opener.postMessage({ type: pdfMessage, index: idx }, parentOrigin);
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = label || 'PDF로 저장';
      }, 2500);
    });
  });
  document.querySelectorAll('[data-print]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = Number(btn.getAttribute('data-print'));
      const article = document.querySelector('.doc[data-idx=\"' + idx + '\"]');
      if (!article) return;
      document.querySelectorAll('.doc').forEach((el) => {
        el.style.display = el === article ? '' : 'none';
      });
      window.print();
      document.querySelectorAll('.doc').forEach((el) => { el.style.display = ''; });
    });
  });
<\/script>
</body></html>`)
  win.document.close()
  return true
}
